// Radha — the deductions desk (073).
//
// Deliberately organised by REASON CODE rather than by customer or by date:
// the job is portfolio management, not case handling. What a deductions lead
// needs first is "which category is bleeding, and what do we actually recover
// from it" — the per-item queue is the second question, not the first.
import React, { useMemo, useState } from "react";

import {
  useDeductions, useDeductionSummary, useReasonCodes, useResolveDeduction,
  deductionErrorMessage, type Deduction, type DeductionStatus,
} from "../hooks/useDeductions";
import { useRole } from "../lib/authSupabase";
import { fmtMoney, fmtMoneyFull } from "../lib/money";
import { useTableSearch, SearchBar, ExportButton } from "../components/TableSearch";

const STATUS_COLOR: Record<DeductionStatus, string> = {
  open: "var(--status-warning)", researching: "var(--accent-purple)",
  valid: "var(--fg-muted)", disputed: "var(--status-critical)",
  recovered: "var(--status-good)", written_off: "var(--fg-faint)",
};
const OPEN_STATES: DeductionStatus[] = ["open", "researching", "valid", "disputed"];

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "9px 8px" };
const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };

function ResolveCell({ d, canWrite }: { d: Deduction; canWrite: boolean }) {
  const resolve = useResolveDeduction();
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState(d.amount);
  const [note, setNote] = useState<string | null>(null);
  const terminal = d.status === "recovered" || d.status === "written_off";

  if (terminal) {
    return (
      <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
        {d.status === "recovered" ? fmtMoneyFull(d.resolved_amount, d.currency) : "—"}
        <span style={{ color: "var(--fg-faint)" }}> · {d.resolved_date}</span>
      </span>
    );
  }
  if (!canWrite) return <span style={{ color: "var(--fg-muted)" }}>—</span>;

  const act = (status: DeductionStatus) => {
    setNote(null);
    resolve.mutate({ id: d.id, status, resolved_amount: Number(amt) || 0 }, {
      onSuccess: () => setOpen(false),
      onError: (e) => setNote(deductionErrorMessage(e)),
    });
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        style={{ padding: "2px 8px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
        resolve
      </button>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      <input type="number" value={amt} onChange={(e) => setAmt(e.target.value)} title="Amount actually recovered"
        style={{ width: 90, padding: "2px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }} />
      <button onClick={() => act("recovered")} disabled={resolve.isPending}
        style={{ padding: "2px 8px", fontSize: 11, border: 0, borderRadius: 5, background: "var(--status-good)", color: "#fff", cursor: "pointer" }}>recovered</button>
      <button onClick={() => act("written_off")} disabled={resolve.isPending}
        style={{ padding: "2px 8px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>write off</button>
      <button onClick={() => setOpen(false)}
        style={{ padding: "2px 6px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>×</button>
      {note && <span style={{ fontSize: 10.5, color: "var(--status-critical)" }}>{note}</span>}
    </span>
  );
}

export function DeductionsPanel() {
  const { data: rows, isLoading, error } = useDeductions();
  const { data: summary } = useDeductionSummary();
  const { data: codes } = useReasonCodes();
  const { canWrite } = useRole();
  const [focus, setFocus] = useState<string | null>(null);

  const label = useMemo(
    () => new Map((codes ?? []).map((c) => [c.code, c.label])), [codes],
  );

  const visible = useMemo(
    () => (rows ?? []).filter((r) => !focus || r.reason_code === focus), [rows, focus],
  );
  const { q, setQ, filtered } = useTableSearch(visible,
    (d) => `${d.customer_name} ${d.reason_code} ${d.claim_reference ?? ""} ${d.status}`);

  if (isLoading) return <p style={{ padding: 24, color: "var(--fg-muted)" }}>Loading…</p>;
  if (error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(error as Error).message}</p>;

  const openRows = (rows ?? []).filter((r) => OPEN_STATES.includes(r.status));
  // Summed from deduction_summary(), NOT from the rows: a deduction carries its
  // invoice's own currency, so adding a EUR one to an INR one directly would
  // misstate the book. The RPC converts each to base first (070).
  const openAmt = summary?.reduce((s, r) => s + Number(r.open_amount || 0), 0) ?? 0;
  const totalRecovered = summary?.reduce((s, r) => s + Number(r.recovered_amount || 0), 0) ?? 0;
  const totalWritten = summary?.reduce((s, r) => s + Number(r.written_off_amount || 0), 0) ?? 0;
  const blendedRate = totalRecovered + totalWritten > 0
    ? Math.round((totalRecovered / (totalRecovered + totalWritten)) * 100) : null;

  return (
    <div>
      <p style={{ color: "var(--fg-muted)", fontSize: 13, margin: "4px 0 16px" }}>
        Short-payments taken against invoices — a promotion claimed, freight charged back, a shortage alleged. The cash
        has already gone, so the question is what to recover and what to concede, not whether the invoice was right.
      </p>

      <div style={{ display: "flex", gap: 24, margin: "4px 0 20px", flexWrap: "wrap" }}>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Open deductions</div>
          <div style={{ font: "600 20px Inter" }}>{openRows.length}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Open value</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-warning)" }}>{fmtMoney(openAmt)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Recovered</div>
          <div style={{ font: "600 20px Inter", color: "var(--status-good)" }}>{fmtMoney(totalRecovered)}</div></div>
        <div><div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Written off</div>
          <div style={{ font: "600 20px Inter" }}>{fmtMoney(totalWritten)}</div></div>
        <div title="Recovered as a share of everything closed. The number a deductions desk is judged on.">
          <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>Recovery rate</div>
          <div style={{ font: "600 20px Inter" }}>{blendedRate == null ? "—" : `${blendedRate}%`}</div></div>
      </div>

      <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ font: "600 15px Inter", marginBottom: 8 }}>By reason</div>
        <div className="sanctuary-scroll-x">
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 }}>
                <th style={th}>Reason</th><th style={th}>Category</th>
                <th style={{ ...th, textAlign: "right" }}>Open</th>
                <th style={{ ...th, textAlign: "right" }}>Open value</th>
                <th style={{ ...th, textAlign: "right" }}>Recovered</th>
                <th style={{ ...th, textAlign: "right" }}>Written off</th>
                <th style={{ ...th, textAlign: "right" }}>Recovery</th>
              </tr>
            </thead>
            <tbody>
              {(summary ?? []).map((s) => (
                <tr key={s.reason_code}
                    onClick={() => setFocus(focus === s.reason_code ? null : s.reason_code)}
                    className={focus === s.reason_code ? "sanctuary-row-active" : undefined}
                    style={{ borderTop: "1px solid var(--border)", cursor: "pointer",
                             borderInlineStart: "2px solid transparent",
                             background: focus === s.reason_code ? "var(--surface-2)" : undefined }}>
                  <td style={td}>
                    {s.label}
                    {!s.usually_valid && (
                      <span title="Usually the seller's to recover rather than the customer's entitlement"
                        style={{ marginInlineStart: 6, fontSize: 10, color: "var(--status-critical)", border: "1px solid currentColor", borderRadius: 4, padding: "0 4px" }}>
                        challengeable
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: "var(--fg-muted)", textTransform: "capitalize" }}>{s.category}</td>
                  <td style={num}>{s.open_count}</td>
                  <td style={num}>{fmtMoney(s.open_amount)}</td>
                  <td style={{ ...num, color: "var(--status-good)" }}>{fmtMoney(s.recovered_amount)}</td>
                  <td style={num}>{fmtMoney(s.written_off_amount)}</td>
                  <td style={num}>{s.recovery_rate == null ? "—" : `${s.recovery_rate}%`}</td>
                </tr>
              ))}
              {(summary ?? []).length === 0 && (
                <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={7}>No deductions recorded yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {focus && (
          <div style={{ marginTop: 8, fontSize: 12 }}>
            <button onClick={() => setFocus(null)}
              style={{ padding: "2px 8px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
              clear filter · {label.get(focus) ?? focus}
            </button>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 12px" }}>
        <SearchBar q={q} setQ={setQ} count={filtered.length} total={(rows ?? []).length} placeholder="Search customer / claim / reason…" />
        <ExportButton name="Deductions" rows={() => filtered.map((d) => ({
          Customer: d.customer_name, Reason: label.get(d.reason_code) ?? d.reason_code,
          Claim: d.claim_reference ?? "", Amount: Number(d.amount), Currency: d.currency,
          Status: d.status, Identified: d.identified_date,
          Recovered: d.resolved_amount ? Number(d.resolved_amount) : "", Resolved: d.resolved_date ?? "",
        }))} />
      </div>

      <div className="sanctuary-card sanctuary-scroll-x" style={{ padding: "4px 14px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12, borderBottom: "1px solid var(--border)" }}>
              <th style={th}>Customer</th><th style={th}>Reason</th><th style={th}>Claim</th>
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
              <th style={th}>Identified</th><th style={th}>Status</th><th style={th}>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((d) => (
              <tr key={d.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{d.customer_name}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{label.get(d.reason_code) ?? d.reason_code}</td>
                <td style={{ ...td, color: "var(--fg-faint)", fontSize: 12 }}>{d.claim_reference ?? "—"}</td>
                <td style={num}>{fmtMoneyFull(d.amount, d.currency)}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{d.identified_date}</td>
                <td style={{ ...td, color: STATUS_COLOR[d.status], textTransform: "capitalize" }}>{d.status.replace(/_/g, " ")}</td>
                <td style={td}><ResolveCell d={d} canWrite={canWrite} /></td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td style={{ ...td, color: "var(--fg-muted)" }} colSpan={7}>Nothing matches.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
